// @flow
// @flow-runtime warn
import { useState, useCallback, useEffect, useMemo } from 'react';
import complement from 'ramda/es/complement';
import findIndex from 'ramda/es/findIndex';
import propEq from 'ramda/es/propEq';
import adjust from 'ramda/es/adjust';
import filter from 'ramda/es/filter';
import update from 'ramda/es/update';
import append from 'ramda/es/append';

import { api, requestsGeneral } from 'app/api';

import type { SolutionType, SolutionsRequestParams, SolutionsRequestResult, SolutionCreateRequestParams, CreateSolutionRequestResult } from './types';


const propNotEq = complement(propEq);
const defaultSolutions = [];

export default function useSolutionsData(params: ?SolutionsRequestParams): any {
  const [solutions, setSolutions] = useState<Array<SolutionType>>(defaultSolutions);
  const [loading, setLoading] = useState(true);


  /**
   *  ЗАПРОС РЕШЕНИЙ ДОМАШНИХ ЗАДАНИЙ
   */

  const handleRequestSolutions = useCallback(async (params_: ?SolutionsRequestParams = params) => {
    if (!params_) return false;

    const solutionsRes: SolutionsRequestResult | null = await api.request(
      requestsGeneral.Homework_GET_solutions, {
        error: 'Не удалось получить решения домашних заданий',
        onChangeLoadingState: setLoading,
        params: params_,
      }
    );

    if (solutionsRes && solutionsRes.data) {
      setSolutions(solutionsRes.data);
      return true;
    }
    return false;
  }, [params]);


  useEffect(() => {
    handleRequestSolutions();
  }, [handleRequestSolutions]);


  /**
   *  ЗАПРОС СОЗДАНИЯ РЕШЕНИЯ ДОМАШНЕГО ЗАДАНИЯ
   */

  const handleRequestCreateSolutions = useCallback(async (params_: SolutionCreateRequestParams) => {
    if (!params_) return null;

    const solutionRes: CreateSolutionRequestResult | null = await api.request(
      requestsGeneral.Homework_POST_create_solution, {
        error: 'Не удалось создать решение домашнего задания',
        params: params_,
      }
    );

    if (solutionRes) {
      setSolutions(append(solutionRes));
      return solutionRes;
    }
    return null;
  }, []);


  /**
   *  ДОБАВЛЕНИЕ ПРИКРЕПЛЕННОГО ФАЙЛА
   */

  const handlePostFile = useCallback(async (
    file: File,
    solutionId: number,
    onProgress?: (progressEvent: { loaded: number, total: number}) => void,
    onSuccess?: () => void,
    onFail?: () => void
  ) => {
    const data = new FormData();
    data.append('file', file);
    const fileRes: SolutionType | null = await api.request(
      requestsGeneral.Homework_POST_solution_file, {
        params: data,
        error: 'Не удалось загрузить файл',
        requestPathParams: {
          solutionId,
        },
        ...(onProgress ? { additionalAxiosOptions: { onUploadProgress: onProgress } } : {}),
      }
    );

    if (fileRes) {
      setSolutions((solutionsState) => {
        const solutionIndex = findIndex(propEq('id', solutionId), solutionsState);
        return update(solutionIndex, fileRes, solutionsState);
      });
      if (onSuccess) {
        onSuccess();
      }
      return true;
    }
    if (onFail) {
      onFail();
    }
    return false;
  }, []);


  /**
   *  УДАЛЕНИЕ ПРИКРЕПЛЕННОГО ФАЙЛА
   */

  const handleDeleteAttachedFile = useCallback(async (fileId: number, solutionId: number) => {
    const deleteRes: '' | null = await api.request(
      requestsGeneral.Homework_DELETE_attached_file, {
        error: 'Не удалось удалить прикрепленный файл',
        requestPathParams: {
          attachedFileId: fileId,
        },
      }
    );

    if (deleteRes !== null) {
      setSolutions((solutionsState) => {
        const solutionIndex = findIndex(propEq('id', solutionId), solutionsState);
        return adjust(solutionIndex, ({ attached_files: attached, ...other }) => ({
          ...other,
          attached_files: filter(propNotEq('id', fileId), attached),
        }), solutionsState);
      });
      return true;
    }
    return false;
  }, []);


  const memoizedResult = useMemo(() => [[solutions, loading], {
    postFile: handlePostFile,
    deleteAttachedFile: handleDeleteAttachedFile,
    createSolution: handleRequestCreateSolutions,
  }], [
    solutions,
    loading,
    handleDeleteAttachedFile,
    handlePostFile,
    handleRequestCreateSolutions,
  ]);

  return memoizedResult;
}
